/**
 * @fileoverview FooterSections component
 * @description This component renders the footer navigation sections
 * @active Client Component
 * @returns {JSX.Element} Footer sections with navigation links
 */

import { Box, Flex, List, ListItem, Text } from "@chakra-ui/react";
import Link from "next/link";

import { RoslindaleText } from "@/components/ui/text/RoslindaleText";
import { r_400_16_130, r_400_20 } from "@/styles/fontStyles";
import { menuItemsFooter } from "@/utils/menuItemsFooter";
import { sentryFeedbackHandler } from "@/utils/sentryFeedback";

/**
 * FooterSections component renders the footer navigation sections
 * @see {@link /src/utils/menuItemsFooter.ts} for footer menu data structure
 * @see {@link /src/components/ui/text/RoslindaleText.tsx} for custom text component
 * @see {@link /src/utils/sentryFeedback.ts} for problem reporting functionality
 * @returns {JSX.Element} Footer sections with navigation links
 */
function FooterSections() {
  return (
    <Flex flexDirection={{ base: "column", md: "row" }} alignItems="flex-start">
      {menuItemsFooter.map((section, index) => (
        <Box
          key={`section-${index}`}
          mr={index < menuItemsFooter.length - 1 ? { md: "40px" } : undefined}
          mb={{ base: "20px", md: "0" }}
        >
          <Text {...r_400_20} color={"footerSection"} mb="18px">
            {section.section}
          </Text>
          <List color={"white"} style={{ listStyleType: "none", padding: 0 }}>
            {section.items.map((item, itemIndex) => (
              <ListItem
                key={`${item.id}-${itemIndex}`}
                style={{
                  margin: `${
                    itemIndex < section.items.length - 1
                      ? "5px 0 17px 0"
                      : "5px 0"
                  }`,
                }}
              >
                <Flex
                  onClick={(e) => {
                    if (item.text === "Report a Problem") {
                      e.stopPropagation();
                      e.preventDefault();
                      sentryFeedbackHandler();
                    }
                  }}
                >
                  <Link
                    href={item.pathName ? item.pathName : ""}
                    target={
                      item.pathName?.includes("http") ? "_blank" : "_self"
                    }
                  >
                    <RoslindaleText
                      customStyle={{
                        ...r_400_16_130,
                        textDecoration: "underline",
                      }}
                    >
                      {item.text}
                    </RoslindaleText>
                  </Link>
                </Flex>
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
    </Flex>
  );
}

export default FooterSections;
